import { createFileRoute } from '@tanstack/react-router';
import { Code, Flex, Table } from '@televet/kibble-ui/chakra';
import { Alert } from '@televet/kibble-ui/Alert';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Select } from '@televet/kibble-ui/Select';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Textarea } from '@televet/kibble-ui/Textarea';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CodeEditor } from '@/components/CodeEditor';
import { toaster } from '@/components/ui/toaster';
import { validateParameters, type ZodFieldDef } from '@/lib/parameterSchema';
import {
  TemplateForm,
  type TemplateFormValues,
  buildTemplateMutationPayload,
} from '../-components/TemplateForm';

export const Route = createFileRoute('/_authenticated/emitter-templates/$templateId/')({
  component: TemplateDetail,
});

function prettifyJsonBody(queryType: string, body: string): string {
  if (queryType !== 'PrismaQuery' && queryType !== 'StaticList') return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function stubForField(def: ZodFieldDef): unknown {
  switch (def.z) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'date':
      return new Date().toISOString();
    case 'enum':
      return def.values?.[0] ?? '';
    case 'array':
      return [];
    case 'relativeDate':
      return {
        _relativeDate: true,
        ref: def.ref ?? 'now',
        offset: def.offset ?? '-1 day',
        format: def.outputFormat ?? 'date',
      };
    default:
      return null;
  }
}

function buildInitialTestParams(
  parameterSchema: unknown,
  defaultParams: Record<string, unknown> | null | undefined
): string {
  if (!parameterSchema || typeof parameterSchema !== 'object' || Array.isArray(parameterSchema)) {
    return '{}';
  }
  const schema = parameterSchema as Record<string, ZodFieldDef>;
  const result: Record<string, unknown> = {};
  for (const [key, def] of Object.entries(schema)) {
    if (!def || typeof def !== 'object' || !def.z) continue;
    if (defaultParams && key in defaultParams) {
      result[key] = defaultParams[key];
    } else if (def.default !== undefined) {
      result[key] = def.default;
    } else if (!def.optional) {
      result[key] = stubForField(def);
    }
  }
  return JSON.stringify(result, null, 2);
}

interface DryRunResult {
  success: boolean;
  rowCount: number;
  durationMs: number;
  results: Record<string, unknown>[];
  error?: string;
}

function TemplateDetail() {
  const { templateId } = Route.useParams();
  const queryClient = useQueryClient();
  const [testParamsInitialized, setTestParamsInitialized] = useState(false);
  const [testParams, setTestParams] = useState('{}');
  const [testResults, setTestResults] = useState<DryRunResult | null>(null);
  const [changeNotes, setChangeNotes] = useState('');

  const templateQuery = useQuery(trpc.emitter.getTemplate.queryOptions({ id: templateId }));
  const template = templateQuery.data;

  const { data: instancesData } = useQuery(
    trpc.emitter.listInstances.queryOptions({ templateId: [templateId] })
  );
  const instances = instancesData?.items;

  useEffect(() => {
    if (!template || testParamsInitialized) return;
    const defaultParams = (template.defaultConfiguration?.parameters ?? null) as Record<
      string,
      unknown
    > | null;
    const initial = buildInitialTestParams(template.parameterSchema, defaultParams);
    setTestParams(initial);
    setTestParamsInitialized(true);
  }, [template, testParamsInitialized]);

  const { mutate: updateTemplate, isPending: isUpdating } = useMutation(
    trpc.emitter.updateTemplate.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Template updated' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'getTemplate']],
        });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listTemplates']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to update',
          description: error.message,
        });
      },
    })
  );

  const { mutate: testTemplate, isPending: isTesting } = useMutation(
    trpc.emitter.testTemplate.mutationOptions({
      onSuccess: (data) => {
        const result = data as DryRunResult;
        setTestResults(result);
        if (result.success) {
          toaster.success({
            title: `Dry run: ${result.rowCount} rows in ${result.durationMs}ms`,
          });
          queryClient.invalidateQueries({
            queryKey: [['emitter', 'getTemplate']],
          });
        } else {
          toaster.error({
            title: 'Dry run failed',
            description: result.error ?? 'Unknown error',
          });
        }
      },
      onError: (error) => {
        toaster.error({
          title: 'Dry run error',
          description: error.message,
        });
      },
    })
  );

  const { mutate: publishTemplate, isPending: isPublishing } = useMutation(
    trpc.emitter.publishTemplate.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: `Published as v${data.versionNumber}`,
        });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'getTemplate']],
        });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listTemplates']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to publish',
          description: error.message,
        });
      },
    })
  );

  const schemaValidation = useMemo(() => {
    if (!template) return null;

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(testParams);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { jsonValid: false as const, error: 'Must be a JSON object' };
      }
    } catch {
      return { jsonValid: false as const, error: 'Invalid JSON' };
    }

    const result = validateParameters(template.parameterSchema, parsed);
    if (result.success) {
      return { jsonValid: true as const, schemaValid: true as const };
    }
    return { jsonValid: true as const, schemaValid: false as const, errors: result.errors };
  }, [testParams, template]);

  if (templateQuery.isLoading) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" py={16}>
          <Spinner size="lg" centered={false} />
        </Flex>
      </PageContainer>
    );
  }

  if (templateQuery.isError || !template) {
    return (
      <PageContainer>
        <Alert
          status="error"
          hideCloseButton
          title="Failed to load template"
          description={templateQuery.error?.message ?? 'Template not found.'}
        />
      </PageContainer>
    );
  }

  const handleUpdate = (values: TemplateFormValues) => {
    const payload = buildTemplateMutationPayload(values);
    if (!payload) {
      toaster.error({
        title: 'Invalid form data',
        description: 'One or more JSON fields contain invalid syntax. Please review and fix them.',
      });
      return;
    }
    // The builder omits an empty hours field, which on update would leave a
    // previously saved value in place. Only the update path may say null --
    // createTemplateSchema rejects it -- so the clear is applied here.
    updateTemplate({
      id: templateId,
      ...payload,
      defaultConfiguration: {
        ...payload.defaultConfiguration,
        dedupeWindowHours: payload.defaultConfiguration.dedupeWindowHours ?? null,
        // Blank means no template limit: instances fall back to the owner default.
        queryTimeoutSeconds: payload.defaultConfiguration.queryTimeoutSeconds ?? null,
      },
    });
  };

  const handleDryRun = () => {
    try {
      const params = JSON.parse(testParams);
      testTemplate({ id: templateId, parameters: params, limit: 10 });
    } catch {
      toaster.error({ title: 'Invalid JSON in test parameters' });
    }
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={4} width="100%">
        <Breadcrumbs
          items={[{ label: 'Templates', to: '/emitter-templates' }, { label: template.name }]}
        />

        <Flex justify="space-between" align="center">
          <Flex align="center" gap={2}>
            <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
              {template.name}
            </Text>
            <Tag
              label={template.isPublished ? `v${template.currentVersion}` : 'Draft'}
              variant={template.isPublished ? 'success' : 'default'}
              size="sm"
            />
          </Flex>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => publishTemplate({ id: templateId, changeNotes })}
            disabled={!template.lastSuccessfulDryRunAt || isPublishing}
            loading={isPublishing}
          >
            Publish
          </Button>
        </Flex>

        {!template.lastSuccessfulDryRunAt && (
          <Alert
            status="warning"
            hideCloseButton
            description="A successful dry-run is required before publishing."
          />
        )}

        <TemplateForm
          title="Edit Template"
          defaultValues={{
            name: template.name,
            description: template.description ?? '',
            queryType: template.queryType,
            connection: template.connection,
            queryBody: prettifyJsonBody(template.queryType, template.queryBody),
            parameterSchema: JSON.stringify(template.parameterSchema, null, 2),
            identityColumns: Array.isArray(template.identityColumns)
              ? (template.identityColumns as string[]).join(', ')
              : '',
            uniqueByColumns: Array.isArray((template as Record<string, unknown>).uniqueByColumns)
              ? ((template as Record<string, unknown>).uniqueByColumns as string[]).join(', ')
              : '',
            defaultEntityType: ((template as Record<string, unknown>).entityType as string) ?? '',
            eventName: template.eventName ?? '',
            sqsQueue: template.sqsQueue ?? '',
            routingKey: template.routingKey ?? '',
            eventStaticProperties: JSON.stringify(template.eventStaticProperties, null, 2),
            eventFieldMapping: JSON.stringify(template.eventFieldMapping, null, 2),
            defaultParameters: JSON.stringify(
              template.defaultConfiguration?.parameters ?? {},
              null,
              2
            ),
            configurability: JSON.stringify(template.configurability ?? {}, null, 2),
            defaultScheduleType: template.defaultConfiguration?.scheduleType ?? '',
            defaultScheduleCron: template.defaultConfiguration?.scheduleCron ?? '',
            defaultDedupeStrategy: template.defaultConfiguration?.dedupeStrategy ?? '',
            defaultDedupeWindowExecutions: template.defaultConfiguration?.dedupeWindowExecutions
              ? String(template.defaultConfiguration.dedupeWindowExecutions)
              : '',
            defaultDedupeWindowDays: template.defaultConfiguration?.dedupeWindowDays
              ? String(template.defaultConfiguration.dedupeWindowDays)
              : '',
            defaultDedupeWindowHours: template.defaultConfiguration?.dedupeWindowHours
              ? String(template.defaultConfiguration.dedupeWindowHours)
              : '',
            defaultDataSourceNamespace: template.defaultConfiguration?.dataSourceNamespace ?? '',
            defaultQueryTimeoutSeconds:
              template.defaultConfiguration?.queryTimeoutSeconds?.toString() ?? '',
          }}
          onSubmit={handleUpdate}
          isSubmitting={isUpdating}
          submitLabel="Save Changes"
        />

        <Card size="sm" py={3} px={4}>
          <Flex direction="column" gap={1.5}>
            <Text fontWeight="medium" fontSize="sm">
              Change Notes
            </Text>
            <Textarea
              size="sm"
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              placeholder="Describe what changed in this version..."
              rows={2}
            />
          </Flex>
        </Card>

        <Card size="sm" py={3} px={4}>
          <Flex direction="column" gap={3}>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Dry Run
              </Text>
              {schemaValidation && (
                <Tag
                  label={
                    !schemaValidation.jsonValid
                      ? schemaValidation.error
                      : schemaValidation.schemaValid
                        ? 'Valid'
                        : 'Schema mismatch'
                  }
                  variant={
                    !schemaValidation.jsonValid
                      ? 'error'
                      : schemaValidation.schemaValid
                        ? 'success'
                        : 'warning'
                  }
                  size="sm"
                />
              )}
            </Flex>
            {instances && instances.length > 0 && (
              <Flex direction="column" gap={1}>
                <Text fontSize="xs" variant="subtle">
                  Load from instance
                </Text>
                <Select
                  size="sm"
                  placeholder="Select instance..."
                  options={instances.map((inst) => ({
                    value: inst.id,
                    label: `${inst.name ?? `${inst.ownerType}:${inst.ownerId}`} v${inst.templateVersion.versionNumber}`,
                  }))}
                  onValueChange={(details) => {
                    const inst = instances.find((i) => i.id === details.value[0]);
                    if (inst) {
                      setTestParams(JSON.stringify(inst.configuration?.parameters ?? {}, null, 2));
                    }
                  }}
                />
              </Flex>
            )}
            <CodeEditor
              value={testParams}
              onChange={setTestParams}
              language="json"
              placeholder='{"clinicId": "abc123", "daysSinceLastVisit": 180}'
              minHeight="100px"
            />
            {schemaValidation?.jsonValid && !schemaValidation.schemaValid && (
              <Flex direction="column" gap={0.5}>
                {schemaValidation.errors.map((err, i) => (
                  <Text key={i} fontSize="2xs" variant="danger" fontFamily="mono">
                    {err}
                  </Text>
                ))}
              </Flex>
            )}
            <Flex>
              <Button
                size="sm"
                onClick={handleDryRun}
                loading={isTesting}
                disabled={!schemaValidation?.jsonValid || isTesting}
              >
                Run Test
              </Button>
            </Flex>

            {testResults && (
              <Flex direction="column" gap={2}>
                <Flex gap={2} align="center">
                  <Tag
                    label={testResults.success ? 'OK' : 'Failed'}
                    variant={testResults.success ? 'success' : 'error'}
                    size="sm"
                  />
                  <Text fontSize="xs" variant="subtle">
                    {testResults.rowCount} rows / {testResults.durationMs}ms
                  </Text>
                </Flex>
                {testResults.error && (
                  <Code colorPalette="red" p={2} fontSize="xs">
                    {testResults.error}
                  </Code>
                )}
                {testResults.results?.length > 0 && (
                  <DryRunResultsTable results={testResults.results} />
                )}
              </Flex>
            )}
          </Flex>
        </Card>

        {template.versions.length > 0 && (
          <Card size="sm" py={3} px={4}>
            <Flex direction="column" gap={2}>
              <Text fontWeight="semibold" fontSize="sm">
                Version History
              </Text>
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader fontSize="2xs">Version</Table.ColumnHeader>
                    <Table.ColumnHeader fontSize="2xs">Created</Table.ColumnHeader>
                    <Table.ColumnHeader fontSize="2xs">Type</Table.ColumnHeader>
                    <Table.ColumnHeader fontSize="2xs">Notes</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {template.versions.map((v) => (
                    <Table.Row key={v.id}>
                      <Table.Cell>
                        <Text fontSize="xs">v{v.versionNumber}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" variant="subtle">
                          {new Date(v.createdAt).toLocaleDateString()}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" fontFamily="mono">
                          {v.queryType}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" variant="subtle">
                          {v.changeNotes ?? '-'}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Flex>
          </Card>
        )}
      </Flex>
    </PageContainer>
  );
}

/** Extracts a stable set of column keys from all result rows, preserving insertion order. */
function collectColumnKeys(results: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const row of results) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

function DryRunResultsTable({ results }: { results: Record<string, unknown>[] }) {
  const columns = useMemo(() => collectColumnKeys(results), [results]);

  return (
    <Flex overflowX="auto">
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            {columns.map((key) => (
              <Table.ColumnHeader key={key} fontSize="2xs">
                {key}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {results.map((row, i) => (
            <Table.Row key={i}>
              {columns.map((key) => (
                <Table.Cell key={key}>
                  <Text fontSize="2xs" maxW="180px" truncated fontFamily="mono">
                    {formatCellValue(row[key])}
                  </Text>
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
}
